import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { getLaboratorios } from '@/modules/users/api/groupsApi'
import { MESES_META, PAISES_META } from '../types/consumoMeta'
import type { ConsumoMeta, ConsumoMetaCreateInput, ConsumoMetaUpdateInput, PaisMeta, TipoEnvio } from '../types/consumoMeta'

const schema = z.object({
  laboratorio_id: z.coerce.number().min(1, 'Selecciona un laboratorio'),
  proyecto: z.string().min(1, 'El proyecto es requerido'),
  envio: z.string().min(1, 'El envío es requerido'),
  descripcion: z.string().optional(),
  total_envio: z.coerce.number().min(0, 'Debe ser mayor o igual a 0'),
  valor: z.coerce.number().min(0, 'Debe ser mayor o igual a 0'),
  tipo_envio: z.enum(['utilidad', 'marketing']),
  mes: z.coerce.number().min(1).max(12),
  pais: z.enum(['ecuador', 'colombia', 'peru', 'mexico', 'chile', 'argentina', 'otro']),
})

type FormData = z.infer<typeof schema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  record?: ConsumoMeta | null
  onSubmit: (data: ConsumoMetaCreateInput | ConsumoMetaUpdateInput, id?: number) => void
  isSubmitting: boolean
}

export function AdminGestionDialog({ open, onOpenChange, record, onSubmit, isSubmitting }: Props) {
  const isEditing = !!record
  const [labPopoverOpen, setLabPopoverOpen] = useState(false)
  const [selectedLabId, setSelectedLabId] = useState<number | null>(null)

  const { data: laboratorios = [] } = useQuery({
    queryKey: ['laboratorios-list'],
    queryFn: getLaboratorios,
    enabled: open,
  })

  const VALOR_DEFAULT: Record<string, number> = { utilidad: 0.03, marketing: 0.09 }

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      tipo_envio: 'utilidad',
      descripcion: '',
      total_envio: 0,
      valor: 0.03,
      mes: new Date().getMonth() + 1,
      pais: 'ecuador' as PaisMeta,
    },
  })

  useEffect(() => {
    if (open) {
      if (record) {
        reset({
          laboratorio_id: record.laboratorio_id,
          proyecto: record.proyecto,
          envio: record.envio,
          descripcion: record.descripcion,
          total_envio: record.total_envio,
          valor: record.valor,
          tipo_envio: record.tipo_envio,
          mes: record.mes,
          pais: record.pais,
        })
        setSelectedLabId(record.laboratorio_id)
      } else {
        reset({ tipo_envio: 'utilidad', descripcion: '', total_envio: 0, valor: 0.03, mes: new Date().getMonth() + 1, pais: 'ecuador' })
        setSelectedLabId(null)
      }
    }
  }, [open, record, reset])

  const handleFormSubmit = (data: FormData) => {
    if (isEditing) {
      onSubmit({
        proyecto: data.proyecto,
        envio: data.envio,
        descripcion: data.descripcion,
        total_envio: data.total_envio,
        valor: data.valor,
        tipo_envio: data.tipo_envio as TipoEnvio,
        mes: data.mes,
        pais: data.pais as PaisMeta,
      }, record.id)
    } else {
      onSubmit({
        laboratorio_id: data.laboratorio_id,
        proyecto: data.proyecto,
        envio: data.envio,
        descripcion: data.descripcion,
        total_envio: data.total_envio,
        valor: data.valor,
        tipo_envio: data.tipo_envio as TipoEnvio,
        mes: data.mes,
        pais: data.pais as PaisMeta,
      } as ConsumoMetaCreateInput)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar registro' : 'Nuevo registro'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {!isEditing && (
            <div className="space-y-1.5">
              <Label>Laboratorio</Label>
              <Popover open={labPopoverOpen} onOpenChange={setLabPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-zinc-400',
                      !selectedLabId && 'text-muted-foreground'
                    )}
                  >
                    <span className="truncate">
                      {selectedLabId
                        ? laboratorios.find((l: any) => l.grupo_id === selectedLabId)?.grupo_nombre
                        : 'Seleccionar laboratorio'}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar laboratorio..." />
                    <CommandList>
                      <CommandEmpty>Sin resultados.</CommandEmpty>
                      <CommandGroup>
                        {laboratorios.map((lab: any) => (
                          <CommandItem
                            key={lab.grupo_id}
                            value={lab.grupo_nombre}
                            onSelect={() => {
                              setSelectedLabId(lab.grupo_id)
                              setValue('laboratorio_id', lab.grupo_id)
                              setLabPopoverOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedLabId === lab.grupo_id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {lab.grupo_nombre}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.laboratorio_id && (
                <p className="text-xs text-red-500">{errors.laboratorio_id.message}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Proyecto</Label>
              <Input {...register('proyecto')} />
              {errors.proyecto && <p className="text-xs text-red-500">{errors.proyecto.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Envío</Label>
              <Input {...register('envio')} />
              {errors.envio && <p className="text-xs text-red-500">{errors.envio.message}</p>}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Descripción <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Input {...register('descripcion')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Total envío</Label>
              <Input type="number" min="0" {...register('total_envio')} />
              {errors.total_envio && <p className="text-xs text-red-500">{errors.total_envio.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Valor unitario (USD)</Label>
              <Input type="number" min="0" step="0.0001" {...register('valor')} />
              {errors.valor && <p className="text-xs text-red-500">{errors.valor.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo de envío</Label>
              <Select
                defaultValue={record?.tipo_envio ?? 'utilidad'}
                onValueChange={(v) => {
                  setValue('tipo_envio', v as TipoEnvio)
                  if (!isEditing) setValue('valor', VALOR_DEFAULT[v] ?? 0)
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="utilidad">Utilidad</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Mes</Label>
              <Select
                defaultValue={String(record?.mes ?? new Date().getMonth() + 1)}
                onValueChange={(v) => setValue('mes', Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESES_META.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>País</Label>
              <Select
                defaultValue={record?.pais ?? 'ecuador'}
                onValueChange={(v) => setValue('pais', v as PaisMeta)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAISES_META.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Guardar cambios' : 'Crear registro'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
